import type { ReactNode } from "react";
import { ArrowRight, ClipboardList, Lightbulb, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import type { CatalogOffering, CatalogSuggestion, Listing, Scored } from "../../services/catalogApi";
import type { QueueEntry } from "./assessment";
import { BuiltBadge, CenteredCard, primaryButton, secondaryButton, textButton } from "./ui";

/** Services (library offerings) the catalog suggested for what the visitor said; they can add them to their plan. */
export function ServiceSuggestions({ offered, cards, added, onToggle }: {
  offered: Scored[];
  cards: CatalogOffering[];
  added: string[];
  onToggle: (id: string) => void;
}) {
  const shown = offered.flatMap((o) => {
    const card = cards.find((c) => c.id === o.id);
    return card ? [{ card, reason: o.reasons[0] }] : [];
  });
  if (!shown.length) return null;
  return (
    <section aria-labelledby="services-title" className="flex w-full flex-col gap-3 text-left">
      <h2 id="services-title" className="flex items-center gap-2 font-display text-xl font-bold text-text-primary">
        <Wrench className="h-5 w-5 text-primary" aria-hidden="true" /> Services that could help
      </h2>
      <ul className="catalog-stagger grid gap-3 sm:grid-cols-2">
        {shown.map(({ card, reason }) => {
          const isAdded = added.includes(card.id);
          return (
            <li key={card.id} className="flex flex-col gap-2 rounded-2xl border border-borderSubtle bg-surface p-5 shadow-sm">
              <span className="font-bold text-text-primary">{card.headline}</span>
              <span className="text-base text-text-secondary">{card.summary}</span>
              {reason && <span className="text-sm font-semibold text-primary">{reason}</span>}
              <button type="button" onClick={() => onToggle(card.id)} aria-pressed={isAdded}
                      className={`${isAdded ? secondaryButton : primaryButton} mt-auto self-start`}>
                {isAdded ? "Added to my plan" : "Add to my plan"}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** The applications and services the visitor kept, and the call to book. Pricing is covered on the call. */
export default function YourPlan({ items, services = [], runs, allSkipped, onRemove, onRemoveService, onBook, onRestart,
                                   extra }: {
  items: QueueEntry[];
  /** Services the visitor added to their plan. */
  services?: CatalogOffering[];
  runs: string | null;
  allSkipped: boolean;
  onRemove: (id: string) => void;
  onRemoveService?: (id: string) => void;
  onBook: () => void;
  onRestart: () => void;
  /** Shown beneath the plan: services the catalog suggests. */
  extra?: ReactNode;
}) {
  const count = (n: number, one: string, many: string) => (n === 1 ? `One ${one}` : `${n} ${many}`);
  const kept = [items.length ? count(items.length, "application", "applications") : "",
                services.length ? count(services.length, "service", "services").toLowerCase() : ""].filter(Boolean);
  return (
    <div className="flex flex-col gap-7">
      {items.length || services.length ? (
        <>
          <p className="text-lg text-text-secondary">
            {kept.join(" and ")} in your plan. We'll go over each one with you.
            {runs && <> {runs}</>}
          </p>
          <ol className="catalog-stagger flex flex-col gap-3">
            {items.map((entry, i) => (
              <li key={entry.listing.id} className="flex flex-wrap items-start gap-4 rounded-2xl border border-borderSubtle bg-surface p-5 shadow-sm">
                <span aria-hidden="true" className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-primary/10 font-extrabold text-primary">{i + 1}</span>
                <div className="flex min-w-0 flex-[1_1_260px] flex-col gap-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-lg font-bold text-text-primary">{entry.listing.name}</span>
                    {entry.listing.temporary && <BuiltBadge />}
                  </div>
                  <span className="text-text-secondary">{entry.listing.headline}</span>
                  <span className="flex flex-wrap gap-x-4 text-sm font-semibold text-text-muted">
                    <span>{entry.fit} fit</span><span>{entry.listing.time_saved}</span>
                  </span>
                </div>
                <button type="button" onClick={() => onRemove(entry.listing.id)} aria-label={`Remove ${entry.listing.name} from my plan`}
                        className="min-h-[44px] px-2 font-bold text-primary">Remove</button>
              </li>
            ))}
            {services.map((service, i) => (
              <li key={service.id} className="flex flex-wrap items-start gap-4 rounded-2xl border border-borderSubtle bg-surface p-5 shadow-sm">
                <span aria-hidden="true" className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-primary/10 font-extrabold text-primary">{items.length + i + 1}</span>
                <div className="flex min-w-0 flex-[1_1_260px] flex-col gap-1.5">
                  <span className="text-lg font-bold text-text-primary">{service.headline}</span>
                  <span className="text-text-secondary">{service.summary}</span>
                  <span className="text-sm font-semibold text-text-muted">A service we set up for you</span>
                </div>
                {onRemoveService && (
                  <button type="button" onClick={() => onRemoveService(service.id)} aria-label={`Remove ${service.headline} from my plan`}
                          className="min-h-[44px] px-2 font-bold text-primary">Remove</button>
                )}
              </li>
            ))}
          </ol>
          {extra}
          <div className="catalog-enter relative flex flex-wrap items-center gap-5 overflow-hidden rounded-2xl bg-gradient-to-r from-primary via-primaryHover to-accentPurple p-6 shadow-xl shadow-primary/25 sm:p-9">
            <div className="pointer-events-none absolute inset-0 bg-blueprint opacity-30" aria-hidden="true" />
            <div className="relative flex min-w-0 flex-[999_1_380px] flex-col gap-1.5">
              <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">Book a call to go over your plan</h2>
              <p className="text-lg text-surfaceSoft">A short call with our team. We'll walk through each application and go over pricing with you.</p>
            </div>
            <button type="button" onClick={onBook}
                    className="group relative inline-flex min-h-[56px] flex-auto items-center justify-center gap-2 rounded-full bg-white px-7 text-lg font-bold text-primary shadow-lg">
              Book a call<ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </button>
          </div>
        </>
      ) : (
        <CenteredCard id="plan-empty-title" icon={<ClipboardList className="h-7 w-7" />}
                      title={allSkipped ? "You skipped everything in your queue" : "Your plan is empty"}
                      actions={<>
                        <Link to="/book" className={primaryButton}>Book a call to design something together</Link>
                        <button type="button" onClick={onRestart} className={secondaryButton}>Go through my queue again</button>
                      </>}>
          <p>
            {allSkipped ? "That's useful to know. Let's design something together, or take another look at your queue."
              : "Add applications from your queue and they will show up here."}
          </p>
          {extra}
        </CenteredCard>
      )}
    </div>
  );
}

export function NothingFits({ said, suggestions, onChange, onBrowse, extra, onPlan }: {
  said: string;
  /** Applications the catalog has but doesn't list, close to what the visitor described. */
  suggestions?: CatalogSuggestion[];
  onChange: () => void;
  onBrowse: () => void;
  /** Services the catalog suggests; once one is added, `onPlan` opens the plan to book it. */
  extra?: ReactNode;
  onPlan?: () => void;
}) {
  return (
    <CenteredCard id="nofit-title" icon={<Lightbulb className="h-7 w-7" />} title="Nothing ready-made fits just yet"
                  actions={<>
                    {onPlan && <button type="button" onClick={onPlan} className={primaryButton}>See my plan</button>}
                    <Link to="/book" className={onPlan ? secondaryButton : primaryButton}>Book a call to design something together</Link>
                    <button type="button" onClick={onChange} className={secondaryButton}>Change my answers</button>
                    <button type="button" onClick={onBrowse} className={textButton}>Browse everything</button>
                  </>}>
      <p>That's normal. Many of our best projects start with a problem just like yours.</p>
      {said && <blockquote className="w-full rounded-xl bg-surfaceSoft px-5 py-3.5 font-semibold text-text-primary">You told us: “{said}”</blockquote>}
      {!!suggestions?.length && (
        <div className="w-full flex flex-col gap-2.5">
          <span className="text-sm font-bold uppercase tracking-wider text-text-muted">Close to what you described — tell us on the call</span>
          <ul className="catalog-stagger flex flex-col gap-2">
            {suggestions.map((s) => (
              <li key={s.id} className="rounded-xl border border-primary/15 bg-white px-5 py-3.5 text-left">
                <span className="block font-bold text-text-primary">{s.name}</span>
                <span className="block text-sm font-semibold text-text-secondary">{s.summary}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {extra}
      <p>Let's design something together on a short call. Bring the problem, and we'll sketch the fix with you.</p>
    </CenteredCard>
  );
}

export function BrowseAll({ listings, offerings, onStart }: {
  listings: Listing[];
  /** Plain-language service cards from the library, shown beneath the applications. */
  offerings?: CatalogOffering[];
  onStart: () => void;
}) {
  return (
    <div className="flex flex-col gap-6">
      <div className="catalog-enter flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-primary/20 bg-primary/5 px-6 py-5">
        <p className="text-lg font-semibold text-text-primary">Want a list made for your business? It takes about 3 minutes.</p>
        <button type="button" onClick={onStart} className={primaryButton}>Get my personal queue</button>
      </div>
      <div className="catalog-stagger grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {listings.map((l) => (
          <div key={l.id} className="flex flex-col gap-2 rounded-2xl border border-borderSubtle bg-surface p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10">
            <span className="font-display text-xl font-bold text-text-primary">{l.name}</span>
            <span className="text-text-secondary">{l.headline}</span>
            <span className="mt-auto pt-2 text-sm font-semibold text-primary">Solves: {l.problems.join(", ")}</span>
            <span className="text-sm font-semibold text-text-muted">Time saved: {l.time_saved}</span>
          </div>
        ))}
      </div>
      {!!offerings?.length && (
        <div className="flex flex-col gap-4">
          <h2 className="flex items-center gap-2 font-display text-xl font-bold text-text-primary">
            <Wrench className="h-5 w-5 text-primary" aria-hidden="true" /> Services we set up alongside
          </h2>
          <div className="catalog-stagger grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {offerings.map((o) => (
              <div key={o.id} className="flex flex-col gap-2 rounded-2xl border border-borderSubtle bg-surface p-6 shadow-sm">
                <span className="font-display text-lg font-bold text-text-primary">{o.headline}</span>
                <span className="text-text-secondary">{o.summary}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
